import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { GridApi } from 'ag-grid-enterprise'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { useCtxDrivers } from '../../formulas/queries/useCtxDrivers'

/**
 * Hook to navigate to driver data page with all unique formula IDs from all account rows
 * Collects all driver-based accounts and extracts unique formula IDs from their sources
 */
export const useNavigateToAllAccountDrivers = () => {
  const navigate = useNavigate()
  const { data: formulas, refetch, isLoading } = useCtxDrivers()

  const navigateToAllAccountDrivers = useCallback(
    async (gridApi?: GridApi<EnhancedStatementRow>) => {
      if (!gridApi) {
        return
      }

      // Show loading message
      const hideLoading = message.loading('Ladataan ajureita...', 0)

      try {
        // If formulas not loaded, fetch them
        let currentFormulas = formulas
        if (!currentFormulas || currentFormulas.length === 0) {
          const result = await refetch()
          currentFormulas = result.data
        }

        if (!currentFormulas || currentFormulas.length === 0) {
          hideLoading()
          message.warning('Ajureita ei löytynyt')
          return
        }

        const uniqueFormulaIds = new Set<number>()
        const regex = /variable\('([^']+)'/g

        // Collect all unique formula IDs from all driver-based account rows
        gridApi.forEachNode(node => {
          const row = node.data
          if (!row || row.rowType !== 'ACCOUNT') {
            return
          }

          if (!row.budgetingMethod?.source || row.budgetingMethod.method !== 'driver') {
            return
          }

          // Parse variable() calls from source
          const matches = Array.from(row.budgetingMethod.source.matchAll(regex))

          for (const m of matches) {
            const code = m[1]
            const found = currentFormulas?.find(f => f.code === code)
            if (found?.id != null) {
              uniqueFormulaIds.add(found.id)
            }
          }
        })

        if (uniqueFormulaIds.size === 0) {
          hideLoading()
          message.warning('Ajureita ei löytynyt riveiltä')
          return
        }

        const formulaIds = Array.from(uniqueFormulaIds)

        // Hide loading and navigate
        hideLoading()
        navigate('/budgeting/drivers', {
          state: {
            formulaIds
          }
        })
      } catch (error) {
        console.error('Error loading formulas:', error)
        hideLoading()
        message.error('Virhe ladattaessa ajureita')
      }
    },
    [navigate, formulas, refetch]
  )

  return navigateToAllAccountDrivers
}
